import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { TableCell, TableHead, TableHeader, TableRow } from '@mdvip/shared/components/ui/table';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentsTableShell } from '@/components/payments/PaymentsTableShell';
import type { PaymentTransaction } from '@/hooks/payments/transactions/transactionsApi';
import { type ReceiptAction, TransactionRow } from './TransactionRow';

interface TransactionsTableBaseProps {
  /** The rows to render. Already paged by the server in `paginate` mode. */
  transactions: PaymentTransaction[];
  state?: 'loaded' | 'loading' | 'error';
  /** Rendered in place of the table when `state` is `'error'`, so each consumer owns its own emphasis. */
  errorContent?: ReactNode;
  title: string;
  /** Disclaimer copy for the header rail. Stays visible in every state. */
  description: string;
  emptyMessage: string;
  onGenerateReceipt?: (transaction: PaymentTransaction, action: ReceiptAction) => void;
  className?: string;
}

/** Full-page mode: server-driven pagination, so the page number is owned by the caller. */
interface PaginatedModeProps {
  paginate: true;
  page: number;
  perPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  limit?: never;
  onViewAll?: never;
  viewAllLabel?: never;
}

/** Compact mode: a short preview with a link out to the full history instead of a pager. */
interface CompactModeProps {
  paginate?: false;
  limit?: number;
  onViewAll?: () => void;
  viewAllLabel?: string;
  page?: never;
  perPage?: never;
  totalCount?: never;
  onPageChange?: never;
}

export type TransactionsTableProps = TransactionsTableBaseProps & (PaginatedModeProps | CompactModeProps);

const DEFAULT_SKELETON_ROWS = 5;

function SkeletonRows({ rows }: { rows: number }) {
  return Array.from({ length: rows }, (_, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows have no identity of their own
    <TableRow key={index}>
      <TableCell className="hidden w-40 sm:table-cell">
        <Skeleton className="h-3.5 w-20" />
      </TableCell>
      <TableCell className="w-auto sm:w-28">
        <Skeleton className="h-3.5 w-16" />
      </TableCell>
      <TableCell className="sm:w-64">
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-8 w-12 shrink-0 rounded-md" />
          <Skeleton className="h-3.5 w-28" />
        </div>
      </TableCell>
      <TableCell className="w-auto sm:w-36">
        <Skeleton className="h-5 w-20 rounded-full" />
      </TableCell>
      <TableCell className="w-12 text-right">
        <div className="flex justify-end">
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
      </TableCell>
    </TableRow>
  ));
}

/**
 * The members-app transaction history table, with the two consumption modes the design's
 * `TableTransactionHistory` defines: `paginate` for the full Past Transactions page, and
 * `limit` + `onViewAll` for the Payment Center's compact Recent Transactions preview. Both render
 * identical rows and columns; only the footer below the table differs.
 *
 * The title and disclaimer live in a left rail on desktop and stack above the table on mobile, and
 * stay mounted through the loading, empty and error states. The Date column drops out below `sm`
 * (the date reappears under the amount) rather than pushing the table into a sideways scroll.
 *
 * Copy that differs per consumer arrives as props, so a second consumer needs no changes here.
 */
export function TransactionsTable(props: TransactionsTableProps) {
  const {
    transactions,
    state = 'loaded',
    errorContent,
    title,
    description,
    emptyMessage,
    onGenerateReceipt,
    className,
  } = props;
  const { t } = useTranslation('members');

  const rows = props.paginate ? transactions : transactions.slice(0, props.limit ?? transactions.length);
  const isEmpty = rows.length === 0;
  const shellState: 'error' | 'loading' | 'empty' | 'loaded' =
    state === 'error' ? 'error' : state === 'loading' ? 'loading' : isEmpty ? 'empty' : 'loaded';

  const skeletonRows = props.paginate ? props.perPage : (props.limit ?? DEFAULT_SKELETON_ROWS);

  const totalPages = props.paginate ? Math.max(1, Math.ceil(props.totalCount / props.perPage)) : 1;
  const showPager = props.paginate && shellState === 'loaded' && props.totalCount > props.perPage;
  const showCount = props.paginate && shellState === 'loaded' && props.totalCount > 0;

  return (
    <PaymentsTableShell
      className={className}
      title={title}
      description={description}
      state={shellState}
      errorContent={errorContent}
      headerAction={
        !props.paginate &&
        props.onViewAll &&
        props.viewAllLabel && (
          <Button variant="link" onClick={props.onViewAll} className="mt-4 h-auto! w-fit gap-1! p-0! text-sm!">
            {props.viewAllLabel}
            <Icon name="arrowRight" size="xs" />
          </Button>
        )
      }
      emptyState={
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-border px-6 py-12 text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Icon name="receipt" size="lg" />
          </div>
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </div>
      }
      aboveTable={
        showCount && (
          <div className="mb-3 flex items-center justify-end">
            <span className="text-xs text-muted-foreground">
              {t('payments.transactions.count', { count: props.totalCount })}
            </span>
          </div>
        )
      }
      tableHeader={
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="hidden sm:table-cell sm:w-40">{t('payments.transactions.columns.date')}</TableHead>
            <TableHead className="w-auto sm:w-28">{t('payments.transactions.columns.amount')}</TableHead>
            <TableHead className="sm:w-64">{t('payments.transactions.columns.account')}</TableHead>
            <TableHead className="w-auto sm:w-36">{t('payments.transactions.columns.type')}</TableHead>
            <TableHead className="text-right sm:w-14">
              <span className="sr-only">{t('payments.transactions.columns.receipt')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
      }
      skeleton={<SkeletonRows rows={skeletonRows} />}
      rows={rows.map((transaction) => (
        <TransactionRow key={transaction.id} transaction={transaction} onGenerateReceipt={onGenerateReceipt} />
      ))}
      footer={
        showPager && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {t('payments.transactions.pager.range', {
                start: (props.page - 1) * props.perPage + 1,
                end: Math.min(props.page * props.perPage, props.totalCount),
                total: props.totalCount,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={props.page <= 1}
                onClick={() => props.onPageChange(props.page - 1)}
              >
                <Icon name="chevronLeft" size="xs" />
                {t('payments.transactions.pager.previous')}
              </Button>
              <span className="px-1 text-xs font-medium text-muted-foreground">
                {t('payments.transactions.pager.pageOf', { page: props.page, totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={props.page >= totalPages}
                onClick={() => props.onPageChange(props.page + 1)}
              >
                {t('payments.transactions.pager.next')}
                <Icon name="chevronRight" size="xs" />
              </Button>
            </div>
          </div>
        )
      }
    />
  );
}
